import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Mic, Loader2, AlertCircle } from 'lucide-react';
import { useTranslate } from '../lib/i18n';
import { useSpeechToTextFlow } from '../hooks/speech-to-text/useSpeechToTextFlow';
import analyticsService from '../services/analyticsService';
import { getBrowserOptimalMimeType, isAudioRecordingSupported } from '../services/audioService';
import { getWaveformColor } from '../utils/colorInterpolation';

// Global constant for maximum recording duration
export const MAX_RECORDING_DURATION_SECONDS = 30;

export type RecordingState =
  | 'idle'
  | 'requesting-permission'
  | 'recording'
  | 'processing'
  | 'error';

export interface VoiceRecordingError {
  message: string;
  type: 'permission' | 'technical' | 'network' | 'unknown';
}

// Map browser MediaStream API errors to our error types
// Based on W3C MediaStream specification and browser implementations
const MEDIA_ERROR_MAP: Record<string, VoiceRecordingError['type']> = {
  // Standard W3C errors
  NotAllowedError: 'permission', // User denied permission or insecure context
  NotFoundError: 'technical', // No media devices found
  OverconstrainedError: 'technical', // Cannot satisfy constraints
  TypeError: 'technical', // Invalid constraints
  SecurityError: 'permission', // Security restrictions
  AbortError: 'technical', // Operation aborted

  // Browser-specific variants
  PermissionDeniedError: 'permission', // Chrome variant of NotAllowedError
  DevicesNotFoundError: 'technical', // Chrome variant of NotFoundError
};

interface VoiceRecorderProps {
  onTranscriptionComplete: (text: string, language?: string) => void;
  onError?: (error: VoiceRecordingError) => void;
  onRecordingStateChange?: (state: RecordingState) => void;
  onClearError?: () => void;
  onMediaStream?: (stream: MediaStream | null) => void;
  onRemainingSecondsChange?: (seconds: number) => void;
  maxDurationSeconds?: number;
  disabled?: boolean;
  className?: string;
  conversationId: string | null;
}

const VoiceRecorder = ({
  onTranscriptionComplete,
  onError,
  onRecordingStateChange,
  onClearError,
  onMediaStream,
  onRemainingSecondsChange,
  maxDurationSeconds = MAX_RECORDING_DURATION_SECONDS,
  disabled = false,
  conversationId,
}: VoiceRecorderProps) => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordingStartTimeRef = useRef<number | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const durationCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // State for circular progress
  const [recordingProgress, setRecordingProgress] = useState<number>(0);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(maxDurationSeconds);
  const [recordingAnnouncement, setRecordingAnnouncement] = useState<string>('');

  const t = useTranslate();

  // Speech-to-text hook for handling transcription - now also provides recordingState
  const {
    recordingState,
    handleRecordingComplete: handleSTTRecordingComplete,
    handleRecordingError: handleSTTRecordingError,
    handleRecordingStart,
    handlePermissionRequest,
    clearError: clearSTTError,
  } = useSpeechToTextFlow({
    onTranscriptionComplete,
    onError,
    conversationId,
  });

  // Notify parent of recording state changes
  useEffect(() => {
    onRecordingStateChange?.(recordingState);
  }, [recordingState, onRecordingStateChange]);

  // Handle clear error - combines STT error clearing with parent callback
  const handleClearError = useCallback(() => {
    if (recordingState === 'error') {
      clearSTTError();
    }
    onClearError?.();
  }, [recordingState, clearSTTError, onClearError]);

  // Clean up resources
  const cleanup = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      mediaStreamRef.current = null;
    }

    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    recordingStartTimeRef.current = null;

    // Clear duration check interval
    if (durationCheckIntervalRef.current) {
      clearInterval(durationCheckIntervalRef.current);
      durationCheckIntervalRef.current = null;
    }

    // Notify parent stream is no longer available
    onMediaStream?.(null);
  }, [onMediaStream]);

  // Stop recording
  const stopRecording = useCallback(
    (isUserInitiated = true) => {
      if (
        mediaRecorderRef.current &&
        recordingState === 'recording' &&
        recordingStartTimeRef.current
      ) {
        // Track analytics for recording cancellation (only if user manually stopped)
        if (isUserInitiated) {
          const durationSeconds = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
          analyticsService.trackEvent({
            eventName: 'speechToTextRecordingFinished',
            properties: {
              durationSeconds,
            },
          });
        }
        // Don't set processing state here - let the hook handle it
        mediaRecorderRef.current.stop();
      }
    },
    [recordingState]
  );

  // Duration monitoring effect - handles max duration checking and progress tracking
  useEffect(() => {
    if (recordingState === 'recording' && recordingStartTimeRef.current) {
      // Start duration checking interval
      durationCheckIntervalRef.current = setInterval(() => {
        if (recordingStartTimeRef.current) {
          const elapsedSeconds = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
          const progress = Math.min(elapsedSeconds / maxDurationSeconds, 1);
          const remaining = Math.max(maxDurationSeconds - elapsedSeconds, 0);

          setRecordingProgress(progress);
          setRemainingSeconds(remaining);
          onRemainingSecondsChange?.(remaining);

          if (elapsedSeconds >= maxDurationSeconds) {
            // Track analytics for max duration reached
            analyticsService.trackEvent({
              eventName: 'speechToTextMaxDurationReached',
              properties: {
                maxDurationSeconds,
              },
            });
            stopRecording(false); // false = not user-initiated
          }
        }
      }, 100); // Update more frequently for smoother progress
    } else {
      // Clear interval and reset when not recording
      if (durationCheckIntervalRef.current) {
        clearInterval(durationCheckIntervalRef.current);
        durationCheckIntervalRef.current = null;
      }
      if (recordingState === 'idle') {
        setRecordingProgress(0);
        setRemainingSeconds(maxDurationSeconds);
        onRemainingSecondsChange?.(maxDurationSeconds);
      }
    }

    // Cleanup on effect change or unmount
    return () => {
      if (durationCheckIntervalRef.current) {
        clearInterval(durationCheckIntervalRef.current);
        durationCheckIntervalRef.current = null;
      }
    };
  }, [recordingState, maxDurationSeconds, stopRecording, onRemainingSecondsChange]);

  // Request microphone permission and start recording
  const startRecording = useCallback(async () => {
    if (!isAudioRecordingSupported()) {
      handleSTTRecordingError({
        message: t('voiceRecorder.notSupported'),
        type: 'technical', // Browser doesn't support recording APIs
      });
      return;
    }

    if (disabled) return;

    try {
      // Signal that we're requesting permission
      handlePermissionRequest();

      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
        },
      });

      mediaStreamRef.current = stream;

      // Surface stream to parent for visualization
      onMediaStream?.(stream);

      // Set up MediaRecorder
      const mimeType = getBrowserOptimalMimeType();
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000, // 128 kbps for good quality
      });

      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });

        // Check if recording has meaningful audio data
        if (audioBlob.size < 1000) {
          // Less than 1KB suggests no audio
          handleSTTRecordingError({
            message: t('voiceRecorder.noAudioDetected'),
            type: 'technical',
          });
          cleanup();
          return;
        }

        // Use STT hook to handle transcription - it will manage the processing state
        handleSTTRecordingComplete(audioBlob, mimeType);
        cleanup();
        onMediaStream?.(null);
      };

      mediaRecorderRef.current.onerror = () => {
        handleSTTRecordingError({
          message: t('voiceRecorder.recordingError'),
          type: 'technical',
        });
        cleanup();
        onMediaStream?.(null);
      };

      // Start recording and signal the state change
      mediaRecorderRef.current.start(1000); // Collect data every second
      handleRecordingStart();
      recordingStartTimeRef.current = Date.now();

      // Announce recording has started for screen readers
      setRecordingAnnouncement(t('voiceRecorder.recordingStarted'));
      // Clear announcement after a short delay to prevent repetition
      setTimeout(() => setRecordingAnnouncement(''), 3000);

      // Track analytics event
      analyticsService.trackEvent({
        eventName: 'speechToTextRecordingStarted',
      });
    } catch (error) {
      let voiceError: VoiceRecordingError;

      if (error instanceof Error) {
        // Use the error mapping to determine error type
        const errorType = MEDIA_ERROR_MAP[error.name] || 'unknown';

        // Set appropriate user-facing message based on error type
        let message: string;
        switch (errorType) {
          case 'permission':
            message = t('voiceRecorder.permissionDenied');
            break;
          case 'technical':
            message =
              error.name === 'NotFoundError'
                ? t('voiceRecorder.notSupported')
                : t('voiceRecorder.recordingError');
            break;
          default:
            message = t('voiceRecorder.recordingError');
        }

        voiceError = { message, type: errorType };
      } else {
        // Fallback for non-Error objects
        voiceError = {
          message: t('voiceRecorder.recordingError'),
          type: 'unknown',
        };
      }

      handleSTTRecordingError(voiceError);
      cleanup();
    }
  }, [
    disabled,
    t,
    handleSTTRecordingComplete,
    handleSTTRecordingError,
    handlePermissionRequest,
    handleRecordingStart,
    clearSTTError,
    cleanup,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Render recording button based on state
  const renderRecordingButton = () => {
    const baseClasses = `flex items-center justify-center rounded-[10px] px-4 py-2 w-[52px] h-[40px] transition-all duration-500 ease-out
         focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
         ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`;

    const iconSize = 20;

    switch (recordingState) {
      case 'idle':
        return (
          <button
            onClick={startRecording}
            disabled={disabled}
            className={`${baseClasses} border border-ring text-foreground hover:bg-muted`}
            aria-label={t('voiceRecorder.startRecording')}
          >
            <Mic size={iconSize} />
          </button>
        );

      case 'requesting-permission':
        return (
          <button
            disabled
            className={`${baseClasses} border border-ring text-foreground opacity-50`}
            aria-label={t('voiceRecorder.permissionRequest')}
          >
            <Loader2 size={iconSize} className="animate-spin" />
          </button>
        );

      case 'recording': {
        // Calculate circle properties - larger radius to fill more space
        const radius = 18; // Increased circle radius
        const circumference = 2 * Math.PI * radius;
        const strokeDashoffset = circumference * (1 - recordingProgress);
        const progressColor = getWaveformColor(remainingSeconds, maxDurationSeconds);

        return (
          <button
            onClick={() => stopRecording(true)}
            className={`${baseClasses} border border-ring text-foreground hover:bg-card`}
            aria-label={t('voiceRecorder.stopRecording')}
          >
            <svg width="40" height="40" viewBox="0 0 40 40" className="transform -rotate-90">
              {/* Background circle */}
              <circle
                cx="20"
                cy="20"
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth="5"
                opacity="0.2"
              />
              {/* Progress circle */}
              <circle
                cx="20"
                cy="20"
                r={radius}
                fill="none"
                stroke={progressColor}
                strokeWidth="5"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                style={{
                  transition: 'stroke-dashoffset 0.1s linear, stroke 0.5s ease-out',
                }}
              />
            </svg>
          </button>
        );
      }

      case 'processing':
        return (
          <button
            disabled
            className={`${baseClasses} border border-ring text-foreground opacity-50`}
            aria-label={t('voiceRecorder.processing')}
          >
            <Loader2 size={iconSize} className="animate-spin" />
          </button>
        );

      case 'error':
        return (
          <button
            onClick={handleClearError}
            className={`${baseClasses} border border-destructive bg-destructive/15 hover:bg-destructive/20 focus:bg-destructive/20 active:bg-destructive/25 text-foreground`}
            aria-label={t('voiceRecorder.recordingError')}
          >
            <AlertCircle size={iconSize} />
          </button>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderRecordingButton()}
      {/* Single announcement for screen readers when recording starts */}
      {recordingAnnouncement && (
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {recordingAnnouncement}
        </div>
      )}
    </>
  );
};

export default VoiceRecorder;
